import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { firstHit, searchApi, totalHits, type GlobalSearchResults, type SearchHit } from "../api/search";

/**
 * Topbar global search. Debounces 250 ms, fires the BE aggregator, shows a
 * dropdown grouped by kind. Esc / outside-click closes; Enter on the input
 * jumps to the first hit if any.
 */
export function GlobalSearch() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<GlobalSearchResults | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!term.trim()) {
      setResults(null);
      return;
    }
    const handle = setTimeout(() => {
      void searchApi
        .search(term.trim(), i18n.resolvedLanguage ?? "en")
        .then((r) => {
          setResults(r);
          setOpen(true);
        })
        .catch(() => setResults(null));
    }, 250);
    return () => clearTimeout(handle);
  }, [term, i18n.resolvedLanguage]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  function go(hit: SearchHit) {
    setOpen(false);
    setTerm("");
    setResults(null);
    navigate(hit.href);
  }

  function handleEnter() {
    if (!results) return;
    const first = firstHit(results);
    if (first) go(first);
  }

  return (
    <div ref={containerRef} style={wrap}>
      <input
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleEnter();
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder={t("action.search", { defaultValue: "Search" }) + "…"}
        style={input}
        aria-label={t("search.ariaLabel", "Global search")}
      />
      {open && results && totalHits(results) > 0 && (
        <div className="dd-scroll" style={dropdown}>
          <Group label={t("search.group.tasks", "Tasks")} hits={results.tmTasks} onPick={go} />
          <Group label={t("search.group.projects", "Projects")} hits={results.tmProjects} onPick={go} />
          <Group label={t("search.group.meetings", "Meetings")} hits={results.tmMeetings} onPick={go} />
          <Group label={t("search.group.products", "Products")} hits={results.products} onPick={go} />
          <Group label={t("search.group.news", "News")} hits={results.news} onPick={go} />
          <Group label={t("search.group.faq", "FAQ")} hits={results.faq} onPick={go} />
          <Group label={t("search.group.tutorials", "Tutorials")} hits={results.tutorials} onPick={go} />
          <Group label={t("search.group.materials", "Materials")} hits={results.materials} onPick={go} />
        </div>
      )}
      {open && results && totalHits(results) === 0 && (
        <div className="dd-scroll" style={dropdown}>
          <p style={{ ...empty, padding: 12 }}>
            {t("search.noResults", 'No results for "{{term}}"', { term: results.term })}
          </p>
        </div>
      )}
    </div>
  );
}

function Group({ label, hits, onPick }: { label: string; hits: SearchHit[]; onPick: (h: SearchHit) => void }) {
  if (hits.length === 0) return null;
  return (
    <div style={group}>
      <div style={groupHead}>{label}</div>
      <ul style={list}>
        {hits.map((h) => (
          <li key={`${h.kind}:${h.id}`}>
            <button type="button" onClick={() => onPick(h)} style={hitBtn}>
              <strong style={{ fontSize: 13, color: "#0b3a6f" }}>{h.title}</strong>
              {h.snippet && (
                <span style={{ display: "block", color: "#6b7280", fontSize: 11, marginTop: 2 }}>
                  {h.snippet.length > 100 ? h.snippet.slice(0, 100) + "…" : h.snippet}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const wrap: React.CSSProperties = { position: "relative", flex: 1, maxWidth: 480 };
const input: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  fontSize: 13,
  background: "#fff",
};
const dropdown: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
  border: "1px solid #e5e7eb",
  maxHeight: 480,
  overflowY: "auto",
  zIndex: 60,
};
const group: React.CSSProperties = { borderBottom: "1px solid #f3f4f6", padding: "4px 0" };
const groupHead: React.CSSProperties = {
  padding: "6px 12px",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "#9ca3af",
  fontWeight: 700,
};
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0 };
const hitBtn: React.CSSProperties = {
  all: "unset",
  cursor: "pointer",
  display: "block",
  padding: "8px 12px",
  width: "100%",
  boxSizing: "border-box",
};
const empty: React.CSSProperties = { color: "#9ca3af", fontSize: 13, margin: 0 };
